// help-manual-view.ts — the FULL-FIDELITY DOM main-menu HELP manual (HTML/DOM UI
// shell plan). The GOLD STANDARD is the C++/WASM HelpControl (game/Help.cpp) and its
// faithful TS canvas port client/src/ui/help.ts: a hypertext viewer over the pack's
// help.txt (an HTML-ish format — Help.cpp:5-12 supported tags) with inline images,
// clickable cross-links, anchors, an Index, and Back navigation. The menu's previous
// DOM HELP rendered the manual as flat TAG-STRIPPED text (menu-controller.stripHelpTags);
// this renders the RAW help.txt with the SAME tag semantics as the canvas, in DOM.
//
// Supported tags (mirrors ui/help.ts's single-pass layouter):
//   <H1>…</H1>  heading on a new line, LARGE font   (the canvas kifntTitle)
//   <H2>…</H2>  heading/body on a new line, normal font
//   <BR>        line break                          <HR> horizontal rule
//   <A NAME="x"> … </A>  a scroll-anchor target (renders nothing of its own)
//   <A HREF="#x">text</A>  a hyperlink → scrolls to the <A NAME="x"> anchor + pushes Back
//   <IMG SRC="file.anir">  an animation strip named "help": its frame 0 → an <img>
//
// NAV MODEL (faithful to the C++ HelpControl, mapped to the DOM idiom):
//   - The whole manual is ONE scrollable document (the natural DOM idiom) rather than the
//     C++ paginated viewport, so kidcNextPage/PrevPage (page-down/page-up by ~85% of the
//     viewport) become a single continuous scroll. Index + Back — the must-haves — are
//     reproduced FAITHFULLY:
//   - INDEX (kidcIndex / DoIndex): scrolls to the top (the TABLE OF CONTENTS) and pushes
//     the current position onto the Back stack (so Back returns from the Index, like the C++
//     m_nchBack — note the C++ DoIndex does NOT push, but every link/Index jump here records
//     where you were so Back is always useful in the single-doc model).
//   - BACK (kidcBack / DoBack): pops the navigation history and scrolls back there.
//   The menu's own BACK-to-menu button is kept DISTINCT (it closes the manual); this
//   viewer's Index/Back operate WITHIN the document.

import { COL_FG, COL_ACCENT, el, FONT_BODY, FONT_TITLE, TEXT_SHADOW } from './chrome.ts';
import { localizeInstructions } from '../ui/inputverbs.ts';

// ── the parsed document model (node-testable — no DOM) ───────────────────────────

/** One laid-out block of the manual, in file order. */
export type HelpBlock =
  | { kind: 'h1'; text: string } // <H1> large heading
  | { kind: 'h2'; runs: HelpRun[] } // <H2> body line (may contain links)
  | { kind: 'text'; runs: HelpRun[] } // raw text outside H1/H2 (with links)
  | { kind: 'hr' } // <HR>
  | { kind: 'img'; src: string } // <IMG SRC="file.anir">
  | { kind: 'anchor'; name: string }; // <A NAME="x"> scroll target

/** An inline run within a text/h2 block: plain text or a cross-link. */
export type HelpRun = { link: false; text: string } | { link: true; text: string; target: string }; // target = the "#x" href (without the leading '#')

export interface HelpDoc {
  blocks: HelpBlock[];
  /** every <A NAME> anchor in file order (the Index links target these). */
  anchors: string[];
}

/** Decode the handful of HTML entities that appear in help.txt (&amp; in "R&D"). */
export function decodeHelpEntities(s: string): string {
  return s
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ');
}

/**
 * Parse the RAW help.txt into an ordered block model (the same tags the canvas
 * HelpControl.Layout walks, Help.cpp:292-590). Whitespace-only text between tags is
 * dropped (the canvas skips leading CR/LF); inline runs preserve link boundaries.
 *
 * This is a STRUCTURAL parse for the DOM (block + run model), not the canvas's
 * pixel-by-pixel single-pass layouter — the DOM does the wrapping/flow itself. The TAG
 * SEMANTICS (which tag means heading/break/rule/anchor/link/image, and the H1↔H2 font
 * weight) match ui/help.ts exactly.
 */
export function parseHelpDoc(raw: string): HelpDoc {
  const blocks: HelpBlock[] = [];
  const anchors: string[] = [];
  // the current open text container: H1 accumulates into a string; H2/text accumulate runs.
  let mode: 'text' | 'h1' | 'h2' = 'text';
  let runs: HelpRun[] = []; // for text / h2
  let h1Text = ''; // for h1

  const flush = (): void => {
    if (mode === 'h1') {
      const t = h1Text.replace(/\s+/g, ' ').trim();
      if (t.length > 0) blocks.push({ kind: 'h1', text: decodeHelpEntities(t) });
      h1Text = '';
    } else {
      // trim leading/trailing whitespace-only runs; drop an all-empty block
      const cleaned = trimRuns(runs);
      if (cleaned.length > 0) blocks.push({ kind: mode === 'h2' ? 'h2' : 'text', runs: cleaned });
      runs = [];
    }
  };
  const pushText = (s: string): void => {
    if (s.length === 0) return;
    const text = decodeHelpEntities(s);
    if (mode === 'h1') {
      h1Text += text;
      return;
    }
    runs.push({ link: false, text });
  };

  let i = 0;
  while (i < raw.length) {
    if (raw[i] !== '<') {
      const lt = raw.indexOf('<', i);
      const stop = lt < 0 ? raw.length : lt;
      // collapse CR/LF runs to a single space (the file wraps mid-paragraph); the DOM flows.
      pushText(raw.slice(i, stop).replace(/[\r\n]+/g, ' '));
      i = stop;
      continue;
    }
    const gt = raw.indexOf('>', i);
    if (gt < 0) {
      pushText(raw.slice(i));
      break;
    }
    const tag = raw.slice(i, gt + 1);
    const up = tag.toUpperCase();
    i = gt + 1;

    if (up === '<H1>') {
      flush();
      mode = 'h1';
      continue;
    }
    if (up === '<H2>') {
      flush();
      mode = 'h2';
      continue;
    }
    if (up === '</H1>' || up === '</H2>') {
      flush();
      mode = 'text';
      continue;
    }
    if (up === '<BR>') {
      flush();
      continue;
    } // a line/paragraph break
    if (up === '<HR>') {
      flush();
      blocks.push({ kind: 'hr' });
      continue;
    }

    if (up.startsWith('<IMG')) {
      const m = /SRC="([^"]*)"/i.exec(tag);
      if (m !== null) {
        flush();
        blocks.push({ kind: 'img', src: m[1] });
      }
      continue;
    }

    if (up.startsWith('<A NAME=')) {
      const m = /NAME="([^"]*)"/i.exec(tag);
      if (m !== null) {
        flush();
        blocks.push({ kind: 'anchor', name: m[1] });
        anchors.push(m[1]);
      }
      continue;
    }

    if (up.startsWith('<A HREF=')) {
      const m = /HREF="#?([^"]*)"/i.exec(tag);
      const target = m !== null ? m[1] : '';
      // the link text is everything up to the matching </A>
      const close = raw.toUpperCase().indexOf('</A>', i);
      const labelRaw = close >= 0 ? raw.slice(i, close) : raw.slice(i);
      // the label may itself contain tags (e.g. a nested <BR> after the link) — but in
      // help.txt the HREF label is plain text; strip any stray inner tags defensively.
      const label = decodeHelpEntities(labelRaw.replace(/<[^>]*>/g, '').replace(/[\r\n]+/g, ' '));
      i = close >= 0 ? close + 4 : raw.length;
      if (target.length > 0 && label.trim().length > 0) {
        // a real cross-link: emit a link run into the current text/h2 container.
        if (mode === 'h1') {
          h1Text += label;
        } else runs.push({ link: true, text: label, target });
      } else {
        pushText(label); // anchorless / empty — plain text (the C++ renders <A> text as text)
      }
      continue;
    }

    // any other tag (</A> stragglers, unknown) → drop, like the canvas skips </ tags.
  }
  flush();
  return { blocks, anchors };
}

/** Trim whitespace-only runs at the ends; collapse runs that became empty. */
function trimRuns(runs: HelpRun[]): HelpRun[] {
  const out = runs.filter((r) => r.link || r.text.length > 0);
  // drop a block that is only whitespace text
  if (out.every((r) => !r.link && r.text.trim().length === 0)) return [];
  return out;
}

// ── the browser viewer (DOM) ─────────────────────────────────────────────────────

export interface HelpManualViewDeps {
  /** the RAW help.txt (tags intact). Empty ⇒ a "Help unavailable" message. */
  raw: string;
  /** Resolve an <IMG SRC="file.anir"> to a PNG data URL (the pack's 'help'-strip frame).
   *  null ⇒ unresolved (the viewer renders the alt text + warns). Absent ⇒ no images. */
  resolveImage?: (src: string) => string | null;
  /** play a GUI sfx (ksfxGui*) on the Index/Back/link taps — wired to the host SoundMgr. */
  playSfx?: (sfx: number) => void;
  /** sfx ids (passed in so this module needn't import the misccontrols ksfx constants,
   *  keeping it node-friendly for the parser tests). */
  sfxTap?: number;
  /** fired whenever the Back history changes (a cross-link click, Index, or Back) so the
   *  host can re-render its Index/Back controls' enabled state (the in-doc BACK enables once
   *  there's somewhere to go back to). The body scroll is view state; no controller round-trip. */
  onNavChange?: () => void;
}

/** The result of mounting the manual into a host element. */
export interface HelpManualHandle {
  /** the scrollable document element (the [data-list] body the menu places in its region). */
  body: HTMLElement;
  /** jump to the Index (TOP) — the kidcIndex affordance. */
  toIndex(): void;
  /** pop the navigation history — the kidcBack affordance. Returns false if empty. */
  goBack(): boolean;
  /** true when the Back history is non-empty (so the menu can enable/disable its Back ctrl). */
  canGoBack(): boolean;
  /** which <IMG> srcs resolved vs not (for verification/reporting). */
  imageReport: { resolved: string[]; unresolved: string[] };
}

/**
 * Render the parsed manual into a scrollable DOM document inside `host`, at the given
 * design scale `s`. Returns a handle exposing the Index/Back navigation the menu wires to
 * its Index/Back controls. The fonts/colours come from chrome.ts so the manual matches the
 * section-only help-view + the rest of the shell.
 */
export function mountHelpManual(
  host: HTMLElement,
  s: number,
  d: HelpManualViewDeps,
): HelpManualHandle {
  const doc = parseHelpDoc(d.raw);
  const resolved: string[] = [];
  const unresolved: string[] = [];

  // the navigation Back stack (the C++ m_nchBack[10] ring) — DOM scrollTop positions.
  const backStack: number[] = [];
  // anchor name → its element (for HREF scroll-to + Index).
  const anchorEls = new Map<string, HTMLElement>();

  const body = el('div', {
    overflowY: 'auto',
    overflowX: 'hidden',
    position: 'relative',
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    padding: `${2 * s}px ${4 * s}px`,
    background: 'rgba(7,22,27,0.55)',
    border: `${Math.max(1, Math.round(s))}px solid rgba(143,208,224,0.35)`,
    color: COL_FG,
    fontFamily: FONT_BODY,
    textShadow: TEXT_SHADOW,
  });
  body.dataset.list = '1'; // exclude from the form zip-in
  body.dataset.helpManual = '1'; // the smoke/test hook

  const px = (u: number): string => `${Math.round(u * s)}px`;
  const bodyFontPx = Math.max(8, Math.round(5.5 * s));

  // scroll to a y, recording the current position so Back returns here.
  const pushAndScroll = (toY: number): void => {
    backStack.push(body.scrollTop);
    body.scrollTo({ top: Math.max(0, toY), behavior: 'auto' });
    d.onNavChange?.();
  };
  const followLink = (target: string): void => {
    const elt = anchorEls.get(target);
    d.playSfx?.(d.sfxTap ?? -1);
    if (elt === undefined) {
      console.warn(`[help] unknown link target "#${target}"`);
      return;
    }
    // the target's offset within the scroll container
    pushAndScroll(elt.offsetTop - Math.round(4 * s));
  };

  if (d.raw.trim().length === 0) {
    const err = el('div', { fontSize: `${bodyFontPx}px`, opacity: '0.85' });
    err.textContent = 'Help is unavailable.';
    body.append(err);
    host.replaceChildren(body);
    return {
      body,
      toIndex: () => {},
      goBack: () => false,
      canGoBack: () => false,
      imageReport: { resolved, unresolved },
    };
  }

  // build a run-list (text + links) into a flowing inline container.
  const renderRuns = (runs: HelpRun[], container: HTMLElement): void => {
    for (const run of runs) {
      if (!run.link) {
        container.append(document.createTextNode(localizeInstructions(run.text)));
        continue;
      }
      const a = el('a', {
        color: COL_ACCENT,
        textDecoration: 'underline',
        textUnderlineOffset: '2px',
        cursor: 'pointer',
      });
      a.textContent = localizeInstructions(run.text);
      a.dataset.helpLink = run.target;
      a.setAttribute('role', 'link');
      a.addEventListener('click', (e) => {
        e.preventDefault();
        followLink(run.target);
      });
      container.append(a);
    }
  };

  for (const block of doc.blocks) {
    switch (block.kind) {
      case 'anchor': {
        // a zero-height scroll target; the following H1/text renders normally.
        const mark = el('div', { position: 'relative', height: '0', overflow: 'visible' });
        mark.dataset.helpAnchor = block.name;
        anchorEls.set(block.name, mark);
        body.append(mark);
        break;
      }
      case 'h1': {
        const h = el('div', {
          fontFamily: FONT_TITLE,
          fontSize: `${Math.max(9, Math.round(8 * s))}px`,
          fontWeight: '400',
          letterSpacing: '0.04em',
          color: COL_FG,
          marginTop: px(4),
          marginBottom: px(2),
        });
        h.textContent = localizeInstructions(block.text);
        h.dataset.helpH1 = '1';
        body.append(h);
        break;
      }
      case 'h2': {
        const p = el('div', {
          fontSize: `${bodyFontPx}px`,
          lineHeight: '1.45',
          fontWeight: '400',
          marginBottom: px(2),
          whiteSpace: 'normal',
          wordBreak: 'break-word',
        });
        renderRuns(block.runs, p);
        body.append(p);
        break;
      }
      case 'text': {
        const p = el('div', {
          fontSize: `${bodyFontPx}px`,
          lineHeight: '1.45',
          marginBottom: px(2),
          whiteSpace: 'normal',
          wordBreak: 'break-word',
        });
        renderRuns(block.runs, p);
        body.append(p);
        break;
      }
      case 'hr': {
        // the canvas HR is three dots; the DOM idiom is a faint rule (cyan accent).
        const hr = el('div', {
          height: '0',
          borderTop: `${Math.max(1, Math.round(s))}px solid rgba(143,208,224,0.35)`,
          margin: `${px(3)} 0`,
        });
        body.append(hr);
        break;
      }
      case 'img': {
        const url = d.resolveImage?.(block.src) ?? null;
        if (url !== null) {
          resolved.push(block.src);
          const img = document.createElement('img');
          img.src = url;
          img.alt = block.src;
          Object.assign(img.style, {
            display: 'block',
            margin: `${px(2)} 0`,
            imageRendering: 'pixelated',
            maxWidth: '100%',
          } as Partial<CSSStyleDeclaration>);
          img.dataset.helpImg = block.src;
          body.append(img);
        } else {
          unresolved.push(block.src);
          console.warn(`[help] could not resolve image "${block.src}" — showing alt text`);
          const ph = el('div', {
            fontSize: `${Math.max(7, Math.round(5 * s))}px`,
            opacity: '0.6',
            fontStyle: 'italic',
            margin: `${px(2)} 0`,
            border: `${Math.max(1, Math.round(s))}px dashed rgba(143,208,224,0.3)`,
            padding: `${px(1)} ${px(2)}`,
            display: 'inline-block',
          });
          ph.textContent = `[${block.src}]`;
          ph.dataset.helpImgMissing = block.src;
          body.append(ph);
        }
        break;
      }
    }
  }

  if (unresolved.length > 0) {
    console.warn(
      `[help] ${unresolved.length}/${resolved.length + unresolved.length} images unresolved: ${unresolved.join(', ')}`,
    );
  }

  host.replaceChildren(body);

  return {
    body,
    toIndex(): void {
      d.playSfx?.(d.sfxTap ?? -1);
      backStack.push(body.scrollTop);
      body.scrollTo({ top: 0, behavior: 'auto' });
      d.onNavChange?.();
    },
    goBack(): boolean {
      const y = backStack.pop();
      if (y === undefined) return false;
      d.playSfx?.(d.sfxTap ?? -1);
      body.scrollTo({ top: y, behavior: 'auto' });
      d.onNavChange?.();
      return true;
    },
    canGoBack(): boolean {
      return backStack.length > 0;
    },
    imageReport: { resolved, unresolved },
  };
}
